<template>
  <validation-observer
    ref="observerAccount"
    v-slot="{ invalid, errors }"
    slim>
    <oie-edit-wrapper :title="t('accountHeader')" :contained="true">
      <template #preview-content>
        <div class="body-smaller">
          <p v-if="userDetailsProxy.customerNumber">
            {{ `${tStatic('customerNumberLabel')}: ${userDetailsProxy.customerNumber}` }}
          </p>
          <p v-if="showCustomerName">
            {{ `${userDetailsProxy.isCompany ? t('companyNameLabel') : t('customerNameLabel')}: ${userDetailsProxy.customerName}` }}
          </p>
          <p>
            {{ `${tStatic('fullName')}: ${userDetailsProxy.firstName} ${userDetailsProxy.lastName}` }}
          </p>
          <p>
            {{ `${tStatic('phoneLabel')}: ${userDetailsProxy.userPhone}` }}
          </p>
          <p>{{ `${tStatic('email')}: ${userDetailsProxy.userEmail}` }}</p>
          <p>{{ `${tStatic('alias')}: ${userDetailsProxy.alias}` }}</p>
          <p v-if="userDetailsProxy.language">
            {{ `${t('userPreferredLanguageLabel')}: ${languageTranslation}` }}
          </p>
          <p>
            {{ `${t('showListPricesLabel')}: ${userDetailsProxy.showListPrices ? tStatic('genericYes') : tStatic('genericNo')}` }}
          </p>
        </div>
      </template>
      <template #edit-actions="{ toggle }">
        <oie-action-wrapper>
          <oie-base-action size="large" theme="skeleton" @click="exit(toggle)">
            {{ tStatic('genericClose') }}
          </oie-base-action>
          <oie-base-action
            size="large"
            @click="save(toggle)">
            {{ tStatic('genericSave') }}
          </oie-base-action>
        </oie-action-wrapper>
      </template>
      <template #edit-content>
        <div class="input-column__container">
          <oie-base-input-v2
            v-if="userDetailsProxy.customerNumber"
            id="my-pages-company-number"
            :value="userDetailsProxy.customerNumber"
            :label="tStatic('customerNumberLabel')"
            readonly></oie-base-input-v2>

          <validation-provider
            v-if="showCustomerName"
            v-slot="{errors}"
            rules="required"
            slim>
            <oie-base-input-v2
              v-model="userDetailsProxy.customerName"
              required
              :errors="errors"
              :label="userDetailsProxy.isCompany ? t('companyNameLabel') : t('customerNameLabel')"
              :placeholder="userDetailsProxy.isCompany ? t('companyNamePlaceholderLabel') : t('customerNamePlaceholderLabel')"></oie-base-input-v2>
          </validation-provider>

          <validation-provider v-slot="{errors}" rules="required" slim>
            <oie-base-input-v2
              v-model="userDetailsProxy.firstName"
              required
              :errors="errors"
              :label="tStatic('firstNameLabel')"
              :placeholder="tStatic('firstNamePlaceholder')"></oie-base-input-v2>
          </validation-provider>

          <validation-provider v-slot="{errors}" rules="required" slim>
            <oie-base-input-v2
              v-model="userDetailsProxy.lastName"
              required
              :errors="errors"
              :label="tStatic('lastNameLabel')"
              :placeholder="tStatic('lastNamePlaceholder')"></oie-base-input-v2>
          </validation-provider>

          <template v-if="activeCountry">
            <validation-provider
              v-if="activeCountry.useVatCode && userDetailsProxy.isCompany"
              v-slot="{errors}"
              :debounce="500"
              :rules="activeCountry ? `vatNumber:${activeCountry.value}` : null"
              slim>
              <oie-vat-input
                v-model="userDetailsProxy.vatNumber"
                :country-code="activeCountry.value"
                :errors="errors"
                v-bind="lockVatNumber ? {readonly: 'true'} : undefined"></oie-vat-input>
            </validation-provider>

            <validation-provider v-slot="{errors}" rules="required|mobile:^[0-9+]+$" slim>
              <oie-phone-input
                v-model="userDetailsProxy.userPhone"
                required
                :errors="errors"
                :label="userDetailsProxy.isCompany ? tStatic('phoneCompanyLabel') : undefined"
                :placeholder="userDetailsProxy.isCompany ? tStatic('phoneCompanyPlaceholder') : undefined"
                :country-code="activeCountry.callingCode"></oie-phone-input>
            </validation-provider>
          </template>

          <oie-base-input-v2
            :value="userDetailsProxy.userEmail"
            :label="tStatic('email')"
            readonly></oie-base-input-v2>

          <oie-base-input-v2
            v-model="userDetailsProxy.alias"
            :label="`${tStatic('alias')}`"
            :placeholder="tStatic('aliasPlaceHolder')"></oie-base-input-v2>

          <oie-base-dropdown
            v-if="userDetailsProxy.language"
            v-model="userDetailsProxy.language"
            :label-text="t('userPreferredLanguageLabel')"
            :options="userDetailsProxy.languageOptions"
            name="user-language"
            :use-filter-functionality="false"
            :default-selected="userDetailsProxy.language">
          </oie-base-dropdown>

          <oie-base-radio-checkbox
            v-model="userDetailsProxy.showListPrices"
            :label="t('showListPricesLabel')"
            class="input-column__checkbox"></oie-base-radio-checkbox>
        </div>
      </template>
    </oie-edit-wrapper>
  </validation-observer>
</template>

<script>
import _ from 'lodash'

export default {
  props: {
    userDetails: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    lockVatNumber: false,
    showCustomerName: false
  }),
  computed: {
    activeCountry() {
      return this.clientModel.countryList.find(c => c.value === this.userDetails.invoiceAddress.countryCode)
    },
    languageTranslation() {
      const language = _.find(this.userDetailsProxy.languageOptions, language => language.value === this.userDetailsProxy.language)
      return language ? language.text : ''
    },
    userDetailsProxy: {
      get() {
        return this.userDetails
      },
      set(val) {
        this.$emit('update:userDetails', val)
      }
    }
  },
  mounted() {
    if(this.userDetails.vatNumber) this.lockVatNumber = true
    this.showCustomerName = !this.userDetails.isMultiUser && (this.userDetails.isCompany || !this.userDetails.fullName ||
        (this.userDetails.fullName && this.userDetails.fullName.length > 0 &&
          this.userDetails.customerName !== this.userDetails.fullName))
  },

  methods: {
    async save(toggle) {
      const valid = await this.$refs.observerAccount.validate()
      if (!valid) return
      this.$emit('save', toggle)
    },
    exit(toggle) {
      this.$emit('exit', toggle)
    }
  }
}
</script>
